import { advanceInvoiceIdsSchema, invoiceAccountingKindSchema } from './advance-invoice.js';
import { mailBusinessSendOptionsSchema, mailBusinessDeliverySchema } from './mail-outbound.js';
import { priceGroupSnapshotSchema, priceResolutionSnapshotSchema } from './price-group.js';
import { productTextSnapshotSchema } from './product-text.js';
import { deliveryPlanningChangeSchema, deliveryPlanningSnapshotSchema, } from './delivery-planning.js';
import { z } from 'zod';
import { QUOTE_VALIDITY_DAYS_MAX } from '../constants/company.js';
import { shippingSummarySchema } from './shipping-summary.js';
import { geographicSubdivisionFields } from './geographic-subdivision.js';
import { DOCUMENT_CURRENCIES, DOCUMENT_ITEMS_MAX_COUNT, DOCUMENT_NUMBER_MAX_LENGTH, DOCUMENT_LANGUAGES, STORED_DOCUMENT_LANGUAGES, DOCUMENT_PAYMENT_TERMS_MAX_DAYS, DOCUMENT_SORT_FIELDS, DOCUMENT_TEXT_MAX_LENGTH, EMAIL_BODY_MAX_LENGTH, EMAIL_CC_MAX_COUNT, EMAIL_SUBJECT_MAX_LENGTH, } from '../constants/documents.js';
import { commaSeparated, countryCodeSchema, decimalStringSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, percentSchema, positiveQuantitySchema, quantitySchema, } from './common.js';
import { customFieldDefinitionSchema, customFieldValuesSchema } from './custom-field.js';
import { customerHeaderLayoutConfigurationSchema, customerHeaderSnapshotSchema, } from './customer-header.js';
import { documentItemTypeSchema, documentPaymentMethodSchema, documentStatusSchema, documentTaxStrategySchema, documentTypeSchema, emailStatusSchema, emailTemplateTypeSchema, paymentStatusSchema, pickingStrategySchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
import { taxDeliveryModeSchema, taxDeterminationSnapshotSchema } from './tax-determination.js';
/** Kopfdaten eines Belegs; Positionen liefert das Belegmodul. */
export const documentSummarySchema = z.object({
    id: idSchema,
    type: documentTypeSchema,
    number: z.string(),
    status: documentStatusSchema,
    paymentStatus: paymentStatusSchema,
    customerId: idSchema,
    documentDate: isoDateSchema,
    dueDate: isoDateSchema.nullable(),
    currency: z.string(),
    netTotal: z.string(),
    taxTotal: z.string(),
    grossTotal: z.string(),
    createdAt: isoDateTimeSchema,
});
export const draftQuoteCreateSchema = z.object({
    contactId: idSchema.nullish(),
    reference: z.string().trim().max(100).nullish(),
    validDays: z.number().int().min(1).max(QUOTE_VALIDITY_DAYS_MAX).optional(),
});
/** Belegliste in der Kundenakte — bewusst schlank und ohne Paginierung. */
export const customerDocumentListQuerySchema = z.object({
    type: documentTypeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const customerDocumentListResponseSchema = z.object({
    data: z.array(documentSummarySchema),
});
// ---------------------------------------------------------------------------
// Anschriften-Snapshot
// ---------------------------------------------------------------------------
/** Zum Belegzeitpunkt eingefrorene Anschrift; Stammdatenänderungen wirken nicht zurück. */
export const documentAddressSchema = z.object({
    recipient: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().max(200).nullable().default(null),
    street: z.string().trim().max(120).nullable().default(null),
    houseNumber: z.string().trim().max(20).nullable().default(null),
    postalCode: z.string().trim().max(20).nullable().default(null),
    city: z.string().trim().max(120).nullable().default(null),
    region: z.string().trim().max(120).nullable().default(null),
    ...geographicSubdivisionFields,
    countryCode: countryCodeSchema.nullable(),
});
/** Internal geographic metadata is excluded from postal PDF/portal output by default. */
export const postalDocumentAddressSchema = documentAddressSchema.omit({
    subdivisionCode: true,
    subdivisionName: true,
});
// ---------------------------------------------------------------------------
// Positionen
// ---------------------------------------------------------------------------
/** Ergebnis eines berechneten Zusatzfeldes, eingefroren zum Speicherzeitpunkt. */
export const documentComputedFieldSchema = z.object({
    value: z.string().nullable(),
    unit: z.string().nullable(),
    decimals: z.number().int().min(0).max(6),
});
export const documentItemSchema = z.object({
    id: idSchema,
    position: z.number().int().min(1),
    itemType: documentItemTypeSchema,
    productId: idSchema.nullable(),
    packagingId: idSchema.nullable(),
    batchId: idSchema.nullable(),
    batchNumber: z.string().nullable(),
    /** Mindesthaltbarkeit der zugeordneten Charge; für die MHD-Spalte im PDF-Designer. */
    expiryDate: isoDateSchema.nullable(),
    articleNumber: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    productTextSnapshot: productTextSnapshotSchema.nullish(),
    quantity: quantitySchema,
    unitCode: z.string(),
    /** Saved base unit; unknown historical units must not be inferred from master data. */
    baseUnit: z.string().nullable().optional(),
    packagingFactor: quantitySchema,
    quantityBase: quantitySchema,
    unitPrice: moneySchema,
    pricingMode: z.enum(['RESOLVED', 'MANUAL']).optional(),
    priceResolution: priceResolutionSnapshotSchema.nullish(),
    discountPct: percentSchema,
    discountAmount: moneySchema,
    taxRatePct: percentSchema,
    netAmount: moneySchema,
    taxAmount: moneySchema,
    grossAmount: moneySchema,
    deliveredQuantity: quantitySchema,
    invoicedQuantity: quantitySchema,
    customFields: customFieldValuesSchema,
    /** Product values belong to this line's product snapshot, never to the line's own fields. */
    productCustomFields: customFieldValuesSchema.optional(),
    /** Werte der Formelfelder; die Formel selbst steht in der Felddefinition. */
    computedFields: z.record(documentComputedFieldSchema),
});
export const documentItemWriteSchema = z
    .object({
    /** Identifies a snapshot in this document; never a client-supplied factor authority. */
    id: idSchema.optional(),
    splitFromItemId: idSchema.optional(),
    packagingChanged: z.boolean().optional(),
    position: z.number().int().min(1).max(9999),
    itemType: documentItemTypeSchema.default('ARTICLE'),
    productId: idSchema.nullish(),
    packagingId: idSchema.nullish(),
    batchId: idSchema.nullish(),
    articleNumber: z.string().trim().max(60).nullish(),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(DOCUMENT_TEXT_MAX_LENGTH).nullish(),
    quantity: quantitySchema.default('1'),
    unitCode: z.string().trim().min(1).max(10).default('PCE'),
    packagingFactor: positiveQuantitySchema.default('1'),
    unitPrice: moneySchema.optional(),
    pricingMode: z.enum(['RESOLVED', 'MANUAL']).optional(),
    priceResolution: priceResolutionSnapshotSchema.nullish(),
    discountPct: percentSchema.default('0'),
    taxRatePct: percentSchema.default('19'),
    customFields: customFieldValuesSchema.default({}),
})
    .superRefine((line, context) => {
    if (line.itemType === 'ARTICLE' &&
        (line.pricingMode !== 'RESOLVED' || !line.productId) &&
        line.unitPrice === undefined)
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['unitPrice'],
            message: 'migration.priceRequired',
        });
})
    .transform((line) => ({ ...line, unitPrice: line.unitPrice ?? '0' }));
// ---------------------------------------------------------------------------
// Beleg
// ---------------------------------------------------------------------------
export const documentTaxBreakdownEntrySchema = z.object({
    taxRatePct: percentSchema,
    netAmount: moneySchema,
    taxAmount: moneySchema,
});
export const documentLanguageSchema = z.enum(DOCUMENT_LANGUAGES);
/** Read-only compatibility for documents frozen before a language was retired. */
export const storedDocumentLanguageSchema = z.enum(STORED_DOCUMENT_LANGUAGES);
export const documentCurrencySchema = z.enum(DOCUMENT_CURRENCIES);
export const exchangeRateSchema = decimalStringSchema({ precision: 18, scale: 8, min: 0 });
export const documentSchema = z.object({
    accountingKind: invoiceAccountingKindSchema.default('STANDARD'),
    advanceInvoiceIds: advanceInvoiceIdsSchema.default([]),
    shippingSummary: shippingSummarySchema.nullish(),
    id: idSchema,
    type: documentTypeSchema,
    number: z.string(),
    status: documentStatusSchema,
    version: z.number().int().min(1),
    priceGroupSnapshot: priceGroupSnapshotSchema.nullish(),
    customerId: idSchema,
    customerNumber: z.string(),
    customerName: z.string(),
    contactId: idSchema.nullable(),
    salesRepId: idSchema.nullable(),
    billingAddress: documentAddressSchema,
    shippingAddress: documentAddressSchema.nullable(),
    shippingAddressId: idSchema.nullish(),
    deliveryPlanningSnapshot: deliveryPlanningSnapshotSchema.nullish(),
    /** Historisch eingefrorene Kundenkopfdaten samt Herkunft sowie das damalige Layout. */
    customerHeaderSnapshot: customerHeaderSnapshotSchema.nullable(),
    customerHeaderLayout: customerHeaderLayoutConfigurationSchema.nullable(),
    documentDate: isoDateSchema,
    serviceDate: isoDateSchema.nullable(),
    validUntil: isoDateSchema.nullable(),
    deliveryDate: isoDateSchema.nullable(),
    dueDate: isoDateSchema.nullable(),
    language: storedDocumentLanguageSchema,
    currency: documentCurrencySchema,
    exchangeRate: exchangeRateSchema,
    accountingBookingDate: isoDateSchema.nullish(),
    accountingTaxDate: isoDateSchema.nullish(),
    accountingRateSource: z.string().nullable().optional(),
    taxStrategy: documentTaxStrategySchema,
    deliveryMode: taxDeliveryModeSchema,
    taxDetermination: taxDeterminationSnapshotSchema.nullable(),
    taxOverrideReason: z.string().nullable(),
    reference: z.string().nullable(),
    paymentMethod: documentPaymentMethodSchema,
    paymentTermId: idSchema.nullable(),
    paymentTermCode: z.string().nullable(),
    paymentTermLabel: z.string().nullable(),
    paymentTermsDays: z.number().int().min(0),
    cashDiscountPct: percentSchema,
    cashDiscountDays: z.number().int().min(0),
    deliveryTerms: z.string().nullable(),
    headerText: z.string().nullable(),
    footerText: z.string().nullable(),
    internalNote: z.string().nullable(),
    discountPct: percentSchema,
    discountAmount: moneySchema,
    shippingAmount: moneySchema,
    netTotal: moneySchema,
    taxTotal: moneySchema,
    grossTotal: moneySchema,
    taxBreakdown: z.array(documentTaxBreakdownEntrySchema),
    paymentStatus: paymentStatusSchema,
    paidAmount: moneySchema,
    isLocked: z.boolean(),
    lockedAt: isoDateTimeSchema.nullable(),
    sourceDocumentId: idSchema.nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    postedAt: isoDateTimeSchema.nullable(),
    cancelledAt: isoDateTimeSchema.nullable(),
    customFields: z.record(z.unknown()),
    origin: z.enum(['INTERNAL', 'CUSTOMER_PORTAL']),
    portalOrder: z
        .object({
        portalUserId: idSchema,
        portalUserName: z.string(),
        submittedAt: isoDateTimeSchema,
        reviewStatus: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
        reviewedAt: isoDateTimeSchema.nullable(),
        reviewedById: idSchema.nullable(),
        reviewReason: z.string().nullable(),
        snapshot: z.record(z.unknown()),
    })
        .nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const documentLinkSchema = z.object({
    id: idSchema,
    type: documentTypeSchema,
    number: z.string(),
    status: documentStatusSchema,
    documentDate: isoDateSchema,
    grossTotal: moneySchema,
});
export const emailLogSchema = z.object({
    delivery: mailBusinessDeliverySchema.optional(),
    id: idSchema,
    documentId: idSchema,
    recipientEmail: z.string(),
    ccEmails: z.array(z.string()),
    subject: z.string(),
    status: emailStatusSchema,
    errorMessage: z.string().nullable(),
    attachmentName: z.string().nullable(),
    sentAt: isoDateTimeSchema,
    sentByName: z.string(),
});
export const cashDiscountSchema = z.object({
    amount: moneySchema,
    payableAmount: moneySchema,
    dueDate: isoDateSchema,
});
export const documentFieldDefinitionSnapshotSchema = z.object({
    schemaVersion: z.literal(1),
    capturedAt: isoDateTimeSchema,
    sourceDocumentId: idSchema.nullable(),
    definitions: z.array(customFieldDefinitionSchema),
});
export const shippingPreviewInputSchema = z.object({
    documentId: idSchema.optional(),
    type: documentTypeSchema,
    items: z.array(documentItemWriteSchema).max(500),
});
export const documentDetailSchema = documentSchema.extend({
    items: z.array(documentItemSchema),
    sourceDocument: documentLinkSchema.nullable(),
    followingDocuments: z.array(documentLinkSchema),
    emailLogs: z.array(emailLogSchema),
    cashDiscount: cashDiscountSchema.nullable(),
    customerEmail: z.string().nullable(),
    /** Aktive sowie für historische Werte benötigte Kopf- und Positionsfelddefinitionen. */
    customFieldDefinitions: z.array(customFieldDefinitionSchema),
    customFieldSnapshotStatus: z.enum(['CAPTURED', 'LEGACY_UNAVAILABLE']).optional(),
});
export const documentListItemSchema = documentSummarySchema.extend({
    accountingKind: invoiceAccountingKindSchema.default('STANDARD'),
    customerNumber: z.string(),
    customerName: z.string(),
    language: storedDocumentLanguageSchema,
    isLocked: z.boolean(),
    itemCount: z.number().int().min(0),
    /** Tage seit Fälligkeit; `null`, solange kein Fälligkeitsdatum gesetzt ist. */
    overdueDays: z.number().int().nullable(),
    openAmount: z.string(),
    salesRepName: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Schreiboperationen
// ---------------------------------------------------------------------------
export const documentEditorDefaultsSchema = z.object({
    quoteValidityDays: z.number().int().min(1).max(QUOTE_VALIDITY_DAYS_MAX),
});
export const documentWriteSchema = z.object({
    accountingKind: invoiceAccountingKindSchema.default('STANDARD'),
    advanceInvoiceIds: advanceInvoiceIdsSchema.default([]),
    customerId: idSchema,
    /** Omitted: customer default on create, saved assignment on update; null: general terms. */
    priceGroupId: idSchema.nullish(),
    contactId: idSchema.nullish(),
    salesRepId: idSchema.nullish(),
    billingAddress: documentAddressSchema,
    shippingAddress: documentAddressSchema.nullish(),
    /** Nur beim Schreiben: mandantensicher aufzulösende Lieferadresse des Kunden. */
    shippingAddressId: idSchema.nullish(),
    deliveryPlanningChange: deliveryPlanningChangeSchema.optional(),
    documentDate: isoDateSchema,
    serviceDate: isoDateSchema.nullish(),
    validUntil: isoDateSchema.nullish(),
    deliveryDate: isoDateSchema.nullish(),
    language: documentLanguageSchema.default('tr'),
    currency: documentCurrencySchema.default('EUR'),
    exchangeRate: exchangeRateSchema.default('1'),
    accountingBookingDate: isoDateSchema.nullish(),
    accountingTaxDate: isoDateSchema.nullish(),
    accountingRateSource: z.string().trim().min(3).max(240).nullish(),
    taxStrategy: documentTaxStrategySchema.default('REGULAR'),
    deliveryMode: taxDeliveryModeSchema.default('UNKNOWN'),
    taxOverrideReason: z.string().trim().min(10).max(1_000).nullish(),
    reference: z.string().trim().max(100).nullish(),
    paymentMethod: documentPaymentMethodSchema.default('INVOICE'),
    paymentTermId: idSchema.nullish(),
    paymentTermsDays: z.number().int().min(0).max(DOCUMENT_PAYMENT_TERMS_MAX_DAYS).default(14),
    cashDiscountPct: percentSchema.default('0'),
    cashDiscountDays: z.number().int().min(0).max(DOCUMENT_PAYMENT_TERMS_MAX_DAYS).default(0),
    deliveryTerms: z.string().trim().max(160).nullish(),
    headerText: z.string().trim().max(DOCUMENT_TEXT_MAX_LENGTH).nullish(),
    footerText: z.string().trim().max(DOCUMENT_TEXT_MAX_LENGTH).nullish(),
    internalNote: z.string().trim().max(DOCUMENT_TEXT_MAX_LENGTH).nullish(),
    discountPct: percentSchema.default('0'),
    shippingAmount: moneySchema.default('0'),
    customFields: customFieldValuesSchema.default({}),
    items: z.array(documentItemWriteSchema).max(DOCUMENT_ITEMS_MAX_COUNT).default([]),
});
export const documentCreateSchema = documentWriteSchema.extend({
    type: documentTypeSchema,
    number: z
        .string()
        .trim()
        .max(DOCUMENT_NUMBER_MAX_LENGTH)
        .regex(/^[^\p{Cc}\p{Cf}]*$/u, 'documentNumber.invalid')
        .nullish()
        .transform((value) => value || undefined),
});
export const documentCustomerPreviewInputSchema = documentCreateSchema.pick({
    customerId: true,
    priceGroupId: true,
    type: true,
    contactId: true,
    billingAddress: true,
    shippingAddress: true,
    shippingAddressId: true,
    language: true,
    currency: true,
    paymentTermsDays: true,
    deliveryTerms: true,
    reference: true,
});
export const documentCustomerPreviewSchema = z.object({
    allowedTypes: z.array(documentTypeSchema),
    customerHeaderSnapshot: customerHeaderSnapshotSchema.nullable(),
    customerHeaderLayout: customerHeaderLayoutConfigurationSchema.nullable(),
});
/** Der Kunde bleibt nach der Anlage fix, damit der Adress-Snapshot konsistent bleibt. */
export const documentUpdateSchema = documentWriteSchema
    .omit({ customerId: true })
    .partial()
    .extend({ version: z.number().int().min(1) });
export const documentConvertSchema = z.object({
    deliveredOnly: z.boolean().optional(),
    targetType: documentTypeSchema,
    /** Omitted: all remaining quantities. Provided: only these source positions. */
    items: z
        .array(z.object({
        sourceItemId: idSchema,
        quantity: positiveQuantitySchema,
    }))
        .min(1)
        .max(DOCUMENT_ITEMS_MAX_COUNT)
        .optional(),
    documentDate: isoDateSchema.optional(),
    /** Chargenauswahl beim Lieferschein; MANUAL überspringt die automatische Zuordnung. */
    pickingStrategy: pickingStrategySchema.default('FEFO'),
    /** Freigabe eines gesperrten Kunden erfordert eine risikofreie Zahlungsart. */
    paymentMethod: documentPaymentMethodSchema.optional(),
    reason: z.string().trim().max(500).nullish(),
});
export const documentLockSchema = z.object({
    version: z.number().int().min(1),
    advancePreviewHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
});
/** Public review only: internal payment, journal and account identities stay server-side. */
export const documentAdvanceFinalPreviewSchema = z.object({
    version: z.number().int().positive(),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/),
    currency: z
        .string()
        .regex(/^[A-Z]{3}$/u)
        .optional(),
    gross: moneySchema.optional(),
    net: moneySchema.optional(),
    tax: moneySchema.optional(),
    appliedGross: moneySchema,
    appliedNet: moneySchema,
    appliedTax: moneySchema,
    residualDue: moneySchema,
    sources: z.array(z.object({
        number: z.string(),
        date: isoDateSchema,
        gross: moneySchema,
        net: moneySchema,
        tax: moneySchema,
        payments: z.array(z.object({ date: isoDateSchema, amount: moneySchema })),
        buckets: z.array(z.object({ rate: z.string(), gross: moneySchema, net: moneySchema, tax: moneySchema })),
    })),
});
export const documentStatusUpdateSchema = z.object({
    status: documentStatusSchema,
    version: z.number().int().min(1),
});
// ---------------------------------------------------------------------------
// Versand
// ---------------------------------------------------------------------------
const emailAddressSchema = z.string().trim().toLowerCase().email().max(200);
export const documentEmailRecipientOptionSchema = z.object({
    email: emailAddressSchema,
    name: z.string(),
    responsibilities: z.array(z.string()),
    recommended: z.boolean(),
    source: z.enum(['CUSTOMER', 'CONTACT']),
});
export const documentEmailPreviewSchema = z.object({
    templateType: emailTemplateTypeSchema,
    to: z.string(),
    cc: z.array(emailAddressSchema).default([]),
    routingConfigured: z.boolean().default(false),
    recipientResponsibilities: z.array(z.string()).default([]),
    recipientOptions: z.array(documentEmailRecipientOptionSchema).default([]),
    subject: z.string(),
    bodyText: z.string(),
    attachmentName: z.string(),
    electronicAttachmentRequired: z.boolean().default(false),
});
export const documentEmailSendSchema = z.object({
    ...mailBusinessSendOptionsSchema.shape,
    to: emailAddressSchema,
    cc: z.array(emailAddressSchema).max(EMAIL_CC_MAX_COUNT).default([]),
    subject: z.string().trim().min(1).max(EMAIL_SUBJECT_MAX_LENGTH),
    bodyText: z.string().trim().min(1).max(EMAIL_BODY_MAX_LENGTH),
    attachPdf: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------
export const documentSortFieldSchema = z.enum(DOCUMENT_SORT_FIELDS);
const booleanFlagSchema = z.preprocess((value) => {
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
    return value;
}, z.boolean().optional());
export const documentsListQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/)
        .optional(),
    type: commaSeparated(documentTypeSchema),
    status: commaSeparated(documentStatusSchema),
    paymentStatus: commaSeparated(paymentStatusSchema),
    customerId: idSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    overdueOnly: booleanFlagSchema,
    openOnly: booleanFlagSchema,
})
    .transform((value) => {
    const [field, order] = (value.sort ?? 'documentDate:desc').split(':');
    const parsedField = documentSortFieldSchema.safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'documentDate',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'desc',
    };
});
export const documentsListResponseSchema = z.object({
    data: z.array(documentListItemSchema),
    meta: paginationMetaSchema,
    facets: z.object({
        byType: z.array(z.object({ value: documentTypeSchema, count: z.number().int() })),
        open: z.number().int(),
        overdue: z.number().int(),
        paid: z.number().int(),
    }),
});
//# sourceMappingURL=document.js.map